import { gsap } from 'gsap';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export function initBooking(): void {
    const page = document.querySelector('.booking-page');
    if (!page) return;

    // ── State ────────────────────────────────────────────
    const today    = new Date();
    today.setHours(0, 0, 0, 0);
    let currentYear  = today.getFullYear();
    let currentMonth = today.getMonth(); // 0-based
    let selectedDate: string | null = null;
    let selectedTime: string | null = null;

    // ── DOM refs ─────────────────────────────────────────
    const monthLabel     = document.getElementById('cal-month-label')!;
    const calGrid        = document.getElementById('cal-grid')!;
    const prevBtn        = document.getElementById('cal-prev')!;
    const nextBtn        = document.getElementById('cal-next')!;
    const slotsGrid      = document.getElementById('slots-grid')!;
    const dateLabelEl    = document.getElementById('selected-date-label')!;
    const bookingSummary = document.getElementById('booking-summary')!;
    const bookingForm    = document.getElementById('booking-form') as HTMLFormElement | null;
    const submitBtn      = document.getElementById('booking-submit-btn') as HTMLButtonElement | null;
    const successOverlay = document.getElementById('booking-success')!;

    // ── Step navigation ───────────────────────────────────
    function goToStep(step: number): void {
        document.querySelectorAll('.booking-panel').forEach(p => p.classList.remove('is-active'));
        document.querySelectorAll('.booking-step').forEach(s => {
            const n = parseInt(s.getAttribute('data-step') || '0');
            s.classList.toggle('is-active', n === step);
            s.classList.toggle('is-done', n < step);
        });
        const panel = document.querySelector<HTMLElement>(`[data-panel="${step}"]`);
        if (panel) {
            panel.classList.add('is-active');
            gsap.fromTo(panel,
                { opacity: 0, x: 30 },
                { opacity: 1, x: 0, duration: 0.45, ease: 'power2.out' }
            );
        }
    }

    document.querySelectorAll('[data-back]').forEach(btn => {
        btn.addEventListener('click', () => {
            const target = parseInt(btn.getAttribute('data-back') || '1');
            goToStep(target);
        });
    });

    // ── Calendar rendering ────────────────────────────────
    function renderCalendar(year: number, month: number): void {
        monthLabel.textContent = `${MONTHS[month]} ${year}`;
        prevBtn.toggleAttribute('disabled', year === today.getFullYear() && month <= today.getMonth());

        // Remove existing day buttons (keep day-name headers = first 7 children)
        const children = Array.from(calGrid.children);
        children.slice(7).forEach(el => el.remove());

        const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        // Convert Sunday=0 → Mon-first offset
        const offset = (firstDay === 0 ? 6 : firstDay - 1);

        for (let i = 0; i < offset; i++) {
            const spacer = document.createElement('div');
            calGrid.appendChild(spacer);
        }

        for (let d = 1; d <= daysInMonth; d++) {
            const dateObj = new Date(year, month, d);
            dateObj.setHours(0, 0, 0, 0);
            const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
            const isPast    = dateObj < today;
            const dateStr   = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'calendar-day';
            btn.textContent = String(d);
            btn.setAttribute('data-date', dateStr);

            if (isWeekend || isPast) {
                btn.classList.add('is-disabled');
                btn.disabled = true;
            } else {
                btn.addEventListener('click', () => selectDate(dateStr, btn));
            }

            if (dateStr === selectedDate) btn.classList.add('is-active');

            calGrid.appendChild(btn);
        }
    }

    prevBtn.addEventListener('click', () => {
        if (currentMonth === 0) { currentMonth = 11; currentYear--; }
        else currentMonth--;
        renderCalendar(currentYear, currentMonth);
    });

    nextBtn.addEventListener('click', () => {
        if (currentMonth === 11) { currentMonth = 0; currentYear++; }
        else currentMonth++;
        renderCalendar(currentYear, currentMonth);
    });

    // ── Date selection ────────────────────────────────────
    async function selectDate(dateStr: string, btn: HTMLButtonElement): Promise<void> {
        selectedDate = dateStr;
        calGrid.querySelectorAll('.calendar-day').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        // Format label
        const [y, m, d] = dateStr.split('-').map(Number);
        const dateObj = new Date(y, m - 1, d);
        dateLabelEl.textContent = dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

        goToStep(2);
        await loadSlots(dateStr);
    }

    // ── Slots loading ─────────────────────────────────────
    async function loadSlots(dateStr: string): Promise<void> {
        slotsGrid.innerHTML = `
            <div class="skel skel--slot"></div>
            <div class="skel skel--slot"></div>
            <div class="skel skel--slot"></div>
            <div class="skel skel--slot"></div>`;

        try {
            const res = await fetch(`/api/booking/slots?date=${dateStr}`);
            const data: { date: string; slots: string[] } = await res.json();

            slotsGrid.innerHTML = '';

            if (!data.slots.length) {
                slotsGrid.innerHTML = '<p class="muted" style="grid-column:1/-1;text-align:center;padding:20px 0">No available slots on this day. Please select another date.</p>';
                return;
            }

            data.slots.forEach((time, i) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'time-slot';
                btn.textContent = time;
                btn.setAttribute('data-time', time);
                btn.style.animationDelay = `${i * 0.06}s`;
                btn.addEventListener('click', () => selectSlot(time, btn));
                slotsGrid.appendChild(btn);
            });

            gsap.from('.time-slot', { opacity: 0, y: 12, stagger: 0.06, duration: 0.4, ease: 'power2.out' });
        } catch {
            slotsGrid.innerHTML = '<p class="muted" style="grid-column:1/-1">Failed to load slots. Please try again.</p>';
        }
    }

    // ── Slot selection ────────────────────────────────────
    function selectSlot(time: string, btn: HTMLButtonElement): void {
        selectedTime = time;
        slotsGrid.querySelectorAll('.time-slot').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');

        setTimeout(() => {
            updateBookingSummary();
            goToStep(3);
        }, 220);
    }

    // ── Summary chip ──────────────────────────────────────
    function updateBookingSummary(): void {
        if (!selectedDate || !selectedTime) return;
        const [y, m, d] = selectedDate.split('-').map(Number);
        const dateLabel = new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        bookingSummary.innerHTML = `
            <div class="booking-summary__chips">
                <span class="pill pill--accent">${dateLabel}</span>
                <span class="pill pill--accent">${selectedTime}</span>
                <span class="pill pill--accent">30 min</span>
            </div>`;
    }

    // ── Form submission ───────────────────────────────────
    bookingForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!selectedDate || !selectedTime) return;

        const fd = new FormData(bookingForm);
        const body = {
            name:    (fd.get('name')    as string).trim(),
            email:   (fd.get('email')   as string).trim(),
            company: (fd.get('company') as string)?.trim() || null,
            service: (fd.get('service') as string) || null,
            message: (fd.get('message') as string)?.trim() || null,
            date:    selectedDate,
            time:    selectedTime
        };

        if (!body.name || !body.email) {
            shakeBtn(submitBtn);
            return;
        }

        setSubmitting(true);

        try {
            const res = await fetch('/api/booking', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (res.ok) {
                showSuccess();
            } else {
                setSubmitting(false);
                shakeBtn(submitBtn);
            }
        } catch {
            setSubmitting(false);
            shakeBtn(submitBtn);
        }
    });

    function setSubmitting(loading: boolean): void {
        if (!submitBtn) return;
        const textEl = submitBtn.querySelector('[data-submit-text]');
        submitBtn.disabled = loading;
        if (textEl) textEl.textContent = loading ? 'Confirming…' : 'Confirm Session';
    }

    function shakeBtn(btn: HTMLButtonElement | null): void {
        if (!btn) return;
        gsap.fromTo(btn, { x: -6 }, { x: 0, duration: 0.5, ease: 'elastic.out(1, 0.3)' });
    }

    // ── Success state ─────────────────────────────────────
    function showSuccess(): void {
        window.japonicsAnalytics?.track('booking_submit', {
            service: bookingForm ? (new FormData(bookingForm).get('service') as string) || 'unspecified' : 'unspecified'
        });
        successOverlay.hidden = false;
        gsap.fromTo(successOverlay,
            { opacity: 0, backdropFilter: 'blur(0px)' },
            { opacity: 1, backdropFilter: 'blur(20px)', duration: 0.6, ease: 'power2.out' }
        );
        gsap.from('.booking-success__inner', {
            opacity: 0, scale: 0.9, y: 40,
            duration: 0.8, ease: 'back.out(1.4)', delay: 0.2
        });
        // Animate enso circle
        gsap.to('.booking-success__enso', {
            rotation: 360, duration: 4, ease: 'power1.inOut', repeat: -1
        });
    }

    // ── Init ──────────────────────────────────────────────
    renderCalendar(currentYear, currentMonth);
}

export function initApplication(): void {
    const form = document.getElementById('application-form') as HTMLFormElement | null;
    if (!form) return;

    const submitBtn = document.getElementById('apply-submit-btn') as HTMLButtonElement | null;
    const errorEl   = document.getElementById('application-error') as HTMLElement | null;
    const successEl = document.getElementById('application-success') as HTMLElement | null;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const fd = new FormData(form);
        const role = (fd.get('role') as string)?.trim() || '';
        const note = (fd.get('note') as string)?.trim() || '';
        const portfolio = (fd.get('portfolio') as string)?.trim() || '';
        const body = {
            name:      (fd.get('name')      as string)?.trim() || '',
            email:     (fd.get('email')     as string)?.trim() || '',
            company:   'Career application',
            service:   role || null,
            summary:   note || null,
            message:   note || `Spontaneous career application submitted via Japonics website. Role: ${role || 'unspecified'}. Portfolio: ${portfolio || 'not provided'}.`,
            timeline:  portfolio || null,
        };

        if (!body.name || !body.email) {
            if (submitBtn) gsap.fromTo(submitBtn, { x: -6 }, { x: 0, duration: 0.5, ease: 'elastic.out(1, 0.3)' });
            return;
        }

        if (submitBtn) { submitBtn.disabled = true; const t = submitBtn.querySelector('[data-submit-text]'); if (t) t.textContent = 'Sending…'; }
        if (errorEl) errorEl.hidden = true;

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (res.ok) {
                window.japonicsAnalytics?.track('application_submit', {
                    role: body.service || 'unspecified'
                });
                if (successEl) {
                    successEl.hidden = false;
                    gsap.fromTo(successEl, { opacity: 0 }, { opacity: 1, duration: 0.5 });
                    gsap.from('.contact-success__inner', { opacity: 0, scale: 0.9, y: 30, duration: 0.7, ease: 'back.out(1.4)', delay: 0.15 });
                    gsap.to('.contact-success__enso', { rotation: 360, duration: 4, ease: 'power1.inOut', repeat: -1 });
                }
            } else {
                if (submitBtn) { submitBtn.disabled = false; const t = submitBtn.querySelector('[data-submit-text]'); if (t) t.textContent = 'Send Application'; }
                if (errorEl) { errorEl.hidden = false; gsap.fromTo(errorEl, { opacity: 0, y: -8 }, { opacity: 1, y: 0, duration: 0.3 }); }
            }
        } catch {
            if (submitBtn) { submitBtn.disabled = false; const t = submitBtn.querySelector('[data-submit-text]'); if (t) t.textContent = 'Send Application'; }
            if (errorEl) { errorEl.hidden = false; }
        }
    });
}

export function initContact(): void {
    const form = document.getElementById('contact-form') as HTMLFormElement | null;
    if (!form) return;

    const submitBtn  = document.getElementById('contact-submit-btn') as HTMLButtonElement | null;
    const errorEl    = document.getElementById('contact-error') as HTMLElement | null;
    const successEl  = document.getElementById('contact-success') as HTMLElement | null;
    const submitTextEl = submitBtn?.querySelector('[data-submit-text]');
    const defaultSubmitText = submitTextEl?.textContent ?? 'Send Inquiry';
    const minTextLength = 3;
    const minMessageLength = 25;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    type ContactField = 'name' | 'email' | 'message';
    const contactFields: ContactField[] = ['name', 'email', 'message'];
    const errorMessages: Record<ContactField | 'submit', string> = {
        name: form.dataset.errorName ?? '',
        email: form.dataset.errorEmail ?? '',
        message: form.dataset.errorMessage ?? '',
        submit: form.dataset.errorSubmit ?? ''
    };

    const getField = (field: ContactField): HTMLInputElement | HTMLTextAreaElement | null =>
        form.elements.namedItem(field) as HTMLInputElement | HTMLTextAreaElement | null;

    const showFieldError = (field: ContactField, message = ''): void => {
        const control = getField(field);
        const errorTarget = form.querySelector<HTMLElement>(`.field-error[data-for="${field}"]`);
        if (errorTarget) {
            errorTarget.textContent = message;
        }

        if (!control) return;

        const hasError = Boolean(message);
        control.setAttribute('aria-invalid', hasError ? 'true' : 'false');
        control.closest('.float-label')?.classList.toggle('is-invalid', hasError);
    };

    const clearFieldErrors = (): void => {
        contactFields.forEach(field => showFieldError(field));
    };

    const focusFirstInvalid = (): void => {
        for (const field of contactFields) {
            const control = getField(field);
            if (control?.getAttribute('aria-invalid') === 'true') {
                control.focus();
                return;
            }
        }
    };

    const validateForm = (): boolean => {
        clearFieldErrors();
        let isValid = true;

        const name = getField('name')?.value.trim() ?? '';
        const email = getField('email')?.value.trim() ?? '';
        const message = getField('message')?.value.trim() ?? '';

        if (name.length < minTextLength) {
            isValid = false;
            showFieldError('name', errorMessages.name);
        }

        if (!emailPattern.test(email)) {
            isValid = false;
            showFieldError('email', errorMessages.email);
        }

        if (message.length < minMessageLength) {
            isValid = false;
            showFieldError('message', errorMessages.message);
        }

        if (!isValid) {
            focusFirstInvalid();
        }

        return isValid;
    };

    const showServerValidation = async (response: Response): Promise<boolean> => {
        try {
            const payload = await response.json() as { fields?: Partial<Record<ContactField, string[]>> };
            if (!payload.fields) {
                return false;
            }

            let hasErrors = false;
            contactFields.forEach(field => {
                if (payload.fields?.[field]?.length) {
                    hasErrors = true;
                    showFieldError(field, errorMessages[field]);
                }
            });

            if (hasErrors) {
                focusFirstInvalid();
            }

            return hasErrors;
        } catch {
            return false;
        }
    };

    contactFields.forEach(field => {
        getField(field)?.addEventListener('input', () => showFieldError(field));
    });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (errorEl) errorEl.hidden = true;

        const fd = new FormData(form);
        const body = {
            name:     (fd.get('name')     as string)?.trim() || '',
            email:    (fd.get('email')    as string)?.trim() || '',
            message:  (fd.get('message')  as string)?.trim() || '',
        };

        if (!validateForm()) {
            shakeEl(submitBtn);
            return;
        }

        setLoading(true);

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (res.ok) {
                showContactSuccess();
            } else {
                setLoading(false);
                const validationHandled = res.status === 400 && await showServerValidation(res);
                if (validationHandled) {
                    shakeEl(submitBtn);
                    return;
                }
                showError();
                shakeEl(submitBtn);
            }
        } catch {
            setLoading(false);
            showError();
            shakeEl(submitBtn);
        }
    });

    function setLoading(loading: boolean): void {
        if (!submitBtn) return;
        submitBtn.disabled = loading;
        if (submitTextEl) submitTextEl.textContent = loading ? 'Sending...' : defaultSubmitText;
    }

    function showError(): void {
        if (!errorEl) return;
        const messageEl = errorEl.querySelector('p');
        if (messageEl && errorMessages.submit) {
            messageEl.textContent = errorMessages.submit;
        }
        errorEl.hidden = false;
        gsap.fromTo(errorEl, { opacity: 0, y: -8 }, { opacity: 1, y: 0, duration: 0.3 });
    }

    function shakeEl(el: HTMLElement | null): void {
        if (!el) return;
        gsap.fromTo(el, { x: -6 }, { x: 0, duration: 0.5, ease: 'elastic.out(1, 0.3)' });
    }

    function showContactSuccess(): void {
        if (!successEl) return;
        window.japonicsAnalytics?.track('contact_form_submit', {
            service: (new FormData(form).get('service') as string) || 'unspecified'
        });
        successEl.hidden = false;
        gsap.fromTo(successEl,
            { opacity: 0, backdropFilter: 'blur(0px)' },
            { opacity: 1, backdropFilter: 'blur(20px)', duration: 0.6, ease: 'power2.out' }
        );
        gsap.from('.contact-success__inner', {
            opacity: 0, scale: 0.9, y: 40,
            duration: 0.8, ease: 'back.out(1.4)', delay: 0.2
        });
        gsap.to('.contact-success__enso', {
            rotation: 360, duration: 4, ease: 'power1.inOut', repeat: -1
        });
    }
}
